import { applyMaintenanceServiceTaskFiltersArrayToExtra } from './tabulatorProgressiveLoading';

const TOP_DEFAULTS = {
  statusValue: 'Otvorený',
  contractorValue: 'Všetky',
  typeValue: 'All',
};

const MASK_TOP_KEYS = new Set(['statusValue', 'contractorValue', 'typeValue', 'status_id']);

/**
 * Saved mask fills an unbuilt table only on the first load.
 * A later rebuild already has a live session snapshot.
 */
export function shouldApplySavedMaskOnUnbuiltTable({ sessionQueryActive } = {}) {
  return !sessionQueryActive;
}

export function serviceTaskTopFiltersToQuery(top, extra = {}) {
  if (!top || typeof top !== 'object') return extra;
  if (top.statusValue === 'Všetky') extra.filter_status = 'Všetky';
  else if (top.statusValue) extra.filter_status = top.statusValue;
  if (top.contractorValue && top.contractorValue !== 'Všetky') {
    if (top.contractorValue === 'Mnou vytvorené') extra.filter_contractorValue = 'Mnou vytvorené';
    else if (top.contractorValue === 'Spomenutý') extra.filter_contractorValue = 'Spomenutý';
    else if (top.contractorValue === 'Vykonávateľ') extra.filter_contractorValue = 'Vykonávateľ';
    else extra.filter_contractor_id = top.contractorValue;
  }
  if (top.typeValue && top.typeValue !== 'All') extra.filter_type = top.typeValue;
  return extra;
}

export function normalizeServiceTaskSorters(sorters) {
  if (!Array.isArray(sorters)) return [];
  return sorters
    .map((sorter) => {
      const column = typeof sorter?.column === 'string'
        ? sorter.column
        : (typeof sorter?.field === 'string' ? sorter.field : null);
      if (!column) return null;
      return { column, field: column, dir: sorter.dir === 'desc' ? 'desc' : 'asc' };
    })
    .filter(Boolean);
}

export function maskHeaderFiltersFromRaw(rawFilters) {
  if (!rawFilters || typeof rawFilters !== 'object' || Array.isArray(rawFilters)) return [];
  return Object.entries(rawFilters)
    .filter(([key]) => !MASK_TOP_KEYS.has(key))
    .map(([field, value]) => ({ field, value }));
}

/**
 * First table build restores the saved mask.
 * Later builds keep the user's current top filters and the live header/sort snapshot.
 */
export function resolveServiceTaskListTableBuiltRestore({
  initialRestore,
  currentTopFilters,
  maskFilters,
  maskSorters,
  sessionHeaderFilters,
  sessionSorters,
} = {}) {
  const current = {
    statusValue: currentTopFilters?.statusValue || TOP_DEFAULTS.statusValue,
    contractorValue: currentTopFilters?.contractorValue || TOP_DEFAULTS.contractorValue,
    typeValue: currentTopFilters?.typeValue || TOP_DEFAULTS.typeValue,
  };

  if (!initialRestore) {
    return {
      phase: 'session',
      ...current,
      headerFilters: Array.isArray(sessionHeaderFilters) ? sessionHeaderFilters : [],
      sorters: normalizeServiceTaskSorters(sessionSorters),
      applyMaskColumnLayout: false,
    };
  }

  const hasMask = maskFilters && typeof maskFilters === 'object' && !Array.isArray(maskFilters);
  if (!hasMask) {
    return {
      phase: 'initial-current',
      ...current,
      headerFilters: [],
      sorters: [],
      applyMaskColumnLayout: false,
    };
  }

  return {
    phase: 'initial-mask',
    statusValue: maskFilters.statusValue || TOP_DEFAULTS.statusValue,
    contractorValue: maskFilters.contractorValue || TOP_DEFAULTS.contractorValue,
    typeValue: maskFilters.typeValue || TOP_DEFAULTS.typeValue,
    headerFilters: maskHeaderFiltersFromRaw(maskFilters),
    sorters: normalizeServiceTaskSorters(maskSorters),
    applyMaskColumnLayout: true,
  };
}

export function applyServiceTaskSessionSnapshotToExtra(extra, snapshot, options = {}) {
  if (!extra || typeof extra !== 'object') return extra;
  applyMaintenanceServiceTaskFiltersArrayToExtra(extra, snapshot?.filters || [], options);
  const sorters = normalizeServiceTaskSorters(snapshot?.sorters);
  if (sorters.length > 0) {
    extra.sorters = JSON.stringify(sorters.map((sorter) => ({ field: sorter.field, dir: sorter.dir })));
  }
  return extra;
}
